import {
    Attachment, Button, ClickEvent, Component, ContextMenuItemCreator, ContextMenuLabelProps, Image, Label, Layout,
    LayoutProps, Panel, SlideoutDecorator, Snackbar, TableRow, Toast
} from "@mcleod/components";
import { Api, BlobUtil, LogManager, MimeTypes, ModelRow, StringUtil } from "@mcleod/core";
import { SendSmsMessageSlideout } from "./SendSmsMessageSlideout";
import { CommonDialogs } from "@mcleod/common";
import { ImageName } from "@mcleod/images";

interface SendSmsMessageLayoutProps extends LayoutProps {
    movementId: string;
    carrierId: string;
    messageText: string;
    orderId: string;
    driverName: string;
    messageId: string;
    deliveryMode: string;
    updateMessage: boolean;
    hours: string;
    recurrenceFrequency: string;
    recurrenceTermination: string;
    driverId: string;
    driverCell: string;
    recipientName: string;
    doAfterSlideOut: () => void;
}


const log = LogManager.getLogger("lme.powerbroker.Sms");

export class SmsUtility{
    private _movementId:string;

    //caption status
    private static FAILED = "Failed"
    private static RECEIVED = "Received";
    private static SENT = "Sent";
    private static CANCELLED = "Canceled";
    private static PENDING = "Pending";
    private static SCHEDULED = "Scheduled";
    private static RECURRING = "Recurring";

    // image colors
    private static SUCCESS = "success";
    private static PRIMARY = "primary";
    private static ERROR = "error";
    private static WARNING = "warning";

    // image names
    private static OUTGOING = ImageName.messagesOutgoingFilled;
    private static INCOMING_MESSAGE_READ = ImageName.messagesIncomingOutlined;
    private static INCOMING_MESSAGE_UNREAD = ImageName.messagesIncomingFilled;
    private static SCHEDULED_ICON = ImageName.clock;
    private static RECURRING_SCHEDULED = ImageName.recurringMessage
    private static FAILED_ICON = ImageName.triangleWarning;
    private static ENVELOPE_UNREAD = ImageName.envelopeUnread;
    private static ENVELOPE_READ = ImageName.envelopeRead;

    private static ARRIVAL_AT_CONSIGNEE = "Arrival at Consignee";
    private static DELIVERY = "Delivery";
    private static OUTGOING_CAPTION = "Outgoing";
    private static OUTGOING_RECURRING_CAPTION = "Outgoing Recurring";
    private static INCOMING_TEXT = "Incoming";
    private static FROM_PREFIX = "From: ";
    private static TO_PREFIX = "To: ";
    private static MARK_AS_UNREAD = "Mark as Unread";
    private static MARK_AS_READ = "Mark as Read";

    private static buttonDelete: Button;
    private static buttonCancel: Button;



    static setMediaComponents(tableRow: TableRow, modelRow: any) {
        const mediaFiles = modelRow.get("media_files");
        const mediaPanel = tableRow.findComponentById("panelMediaMessages") as Panel;
        if (!mediaPanel) {
            return;
        }

        // Clear any existing attachments from the panel (important for row reuse)
        mediaPanel.removeAll();

        if (!mediaFiles || !Array.isArray(mediaFiles) || mediaFiles.length === 0) {
            return;
        }

        let count = 1;
        for (const mediaFile of mediaFiles) {
            try {
                const mediaName = mediaFile.media_name || "";
                const mediaBytes = mediaFile.media_bytes || "";

                if (StringUtil.isEmptyString(mediaBytes)) {
                    continue;
                }

                const fileExtension = mediaName.split('.').pop()?.toLowerCase() || 'tmp';
                const fileName = `Image${count++}.${fileExtension}`;

                const attachment = new Attachment({
                    displayThumbnail: true,
                    showSVG: true,
                    fileBase64: mediaBytes,
                    fileName: fileName,
                    customThumbnailSize: 100,
                    allowRemoveThumbnailOption: false,
                    printable: true,
                    printableDuringAdd: true,
                    printableDuringSearch: true,
                    printableDuringUpdate: true
                });

                mediaPanel.add(attachment);
            } catch (error) {
                log.error(`Error creating attachment for media file:`, error);
            }
        }
    }

    private static async createAttachment(parentRowId: any, mediaName: string, fileName: string): Promise<Attachment> {
        try {
            const imageBytes = await this.getImageBytesForMedia(parentRowId, mediaName);
            return new Attachment({
                displayThumbnail: true,
                showSVG: true,
                fileBase64: imageBytes,
                fileName: fileName,
                customThumbnailSize: 100,
                allowRemoveThumbnailOption: false,
                printable:true,
                printableDuringAdd:true,
                printableDuringSearch:true,
                printableDuringUpdate:true

            });
        } catch (error) {
            log.error(`Error fetching image bytes for mediaName: ${mediaName}`, error);
            throw error;
        }
    }

    private static async getImageBytesForMedia(parentRowId: string | number, mediaName: string): Promise<string> {
        try {
            const response = await Api.search("lme/powerbroker/mms-media", {
                parent_row_id: parentRowId,
                media_name: mediaName
            });

            if (response?.data?.length > 0) {
                for (const record of response.data) {
                    const dbMediaName = record["mms_media.media_name"];
                    const media = record["mms_media.media"];

                    const imageName = mediaName.trim();
                    const dbImageName = dbMediaName.trim();

                    if (!StringUtil.isEmptyString(imageName) && StringUtil.equalsIgnoreCase(imageName, dbImageName) && media) {
                        // Clean and return the media bytes as a string
                        return media.replace(/\[|\]/g, "").toString();
                    }
                }
            }
            return "";
        } catch (error) {
            log.error("Error fetching media:", error);
            return "";
        }
    }


    static formatDate(sendDate: string): string | null {
        try {
            if (!sendDate || sendDate.trim() === "") {
                return "";
            }

            const inputFormat: Intl.DateTimeFormatOptions = {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: 'numeric',
                minute: 'numeric',
                second: 'numeric'
            };

            const date: Date = new Date(sendDate);
            const formatter = new Intl.DateTimeFormat('en-US', inputFormat);
            const formattedDate: string = formatter.format(date);

            // Adjust the output format if needed
            const outputFormat: Intl.DateTimeFormatOptions = {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: 'numeric',
                minute: 'numeric',
                hour12: true
            };

            const formattedOutput = new Intl.DateTimeFormat('en-US', outputFormat).format(date);

            return formattedOutput.replace(',', '');
        } catch (error) {
            return null;
        }
    }

    markStatusOnClick(event: ClickEvent) {
        const messageStatus = event.target as Label;
        const tableRow = TableRow.getContainingTableRow(messageStatus);
        const readStatusImage = tableRow.findComponentById("readStatusImage");
        const modelRow = tableRow.data;
        const messageRowId = modelRow.get("id");
        const messageRead = messageStatus.text === "Mark as Read";
        SmsUtility.updateMessageStatusLabel(messageRowId, messageRead, messageStatus, readStatusImage);
    }

    static updateMessageStatusLabel(messageId: string, messageRead: boolean, messageStatus: Label, readStatusImage: Component) {
        Api.post("lme/powerbroker/update-message-read", {
            message_id: messageId,
            message_read: messageRead
        }).then(response => {
            messageStatus.visible = false;
            readStatusImage.visible = false;
        });
    }


    static labelMediaMessageOnClick(event: ClickEvent, labelMediaName: string) {
        const row: TableRow = TableRow.getContainingTableRow(event.target as Component);
        const modelRow = row.data;
        const parentRowId = modelRow.get("parent_row_id");

        Api.search("lme/powerbroker/mms-media", {
            parent_row_id: parentRowId,
            media_name: labelMediaName
        }).then(response => {
            let imageFound = false;
            const mediaName = response?.data[0]?.["mms_media.media_name"];
            const media = response?.data[0]?.["mms_media.media"];
            if (StringUtil.equalsIgnoreCase(mediaName, labelMediaName) && media !== undefined) {
                imageFound = true;
                const cleanedBytesString = media.replace(/\[|\]/g, "");
                const blob = BlobUtil.toBlob(cleanedBytesString, MimeTypes.PNG);
                const imageUrl = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = imageUrl;
                link.target = '_blank';
                link.click();
                URL.revokeObjectURL(imageUrl);
            }
            if (!imageFound) {
                Snackbar.showWarningSnackbar("Image preview failed.");
            }
        });
    }

    static appendTextStopToOptOut(): string {
        return  '\n\nText back "stop" to opt-out of future messages.';
    }

    static setLayoutProps(row: ModelRow, updateMessage: boolean, isReply: boolean, doAfterSlideOut?: () => void): Partial<SendSmsMessageLayoutProps> {
        const driverName = (!row?.isNull("carrier_driver") && !row?.getBoolean("opted_out_driver_one"))
            ? row?.get("carrier_driver")
            : row?.get("override_driver2_nm");

        const messageText = isReply ? this.appendTextStopToOptOut() : row?.get("message_text");

        return {
            movementId: row?.get("movement_id", ""),
            orderId: row?.get("order_id", ""),
            carrierId: row?.get("carrier_id", ""),
            messageText: messageText || "",
            driverName: driverName || row?.get("driver_name", ""),
            messageId: row?.get("id", ""),
            deliveryMode: row?.get("delivery_mode", ""),
            updateMessage: updateMessage,
            hours: row?.get("hours", ""),
            recurrenceFrequency: row?.get("recurrence_frequency", ""),
            recurrenceTermination: row?.get("recurrence_termination", ""),
            doAfterSlideOut
        };
    }

    static showSendSmsMessageSlideout(layoutProps: Partial<SendSmsMessageLayoutProps>) {
        const layout = Layout.getLayout("lme/powerbroker/SendSmsMessageSlideout", layoutProps) as SendSmsMessageSlideout;

        const button = layoutProps.updateMessage ? layout.buttonUpdate : layout.buttonSend;
        const buttonUpdate = layout.buttonUpdate;
        button.visible = true;

        if(layoutProps.updateMessage) {
            buttonUpdate.enabled = true;
        }

        const slideoutDecorator = new SlideoutDecorator({
            title: layoutProps.updateMessage ? "Update SMS Message" : "Send SMS Message",
            layout: layout,
            fillVerticalSpace: true,
            width: 1200,
            overlayProps: { greyedBackground: true },
            addlComponents: [button,buttonUpdate],
            doAfterSlideIn: async (decorator: SlideoutDecorator) => {
                await layout.mainDataSource.search({ movement_id: layoutProps.movementId });
                await this.setSmsLayoutFields(layout,layoutProps,layoutProps.updateMessage)
            },
            doAfterSlideOut: layoutProps.doAfterSlideOut,
            onClose: (cancelled: boolean) => layout.slideOut()
        });
    }

    private static async setSmsLayoutFields(layout: SendSmsMessageSlideout, layoutProps: Partial<SendSmsMessageLayoutProps>, updateMessage: boolean) {
        if(!StringUtil.isEmptyString(layoutProps.messageText)) {
            layout.smsMessageText.text = layoutProps.messageText;
        }
        layout.sourceCarrierDriverContacts.search({
            carrier_id: layoutProps.carrierId,
            driver_name: StringUtil.isEmptyString(layoutProps.recipientName) ? layoutProps.driverName : layoutProps.recipientName
        }).then(async () => {
            const data : any = layout.sourceCarrierDriverContacts.data;
            for (const row of data) {
                const { driverId, driverName, driverCell } = layoutProps;
                if(!StringUtil.isEmptyString(row.get("id")) && StringUtil.equalsIgnoreCase(row.get("id"), driverId)){
                    layout.setDefaultsViaRow(row);
                    break;
                }
                if(StringUtil.equalsIgnoreCase(row.get("driver_name"), driverName) && StringUtil.equalsIgnoreCase(row.get("cell_phone_number"), driverCell)){
                    layout.setDefaultsViaRow(row);
                    break;
                }
                if(StringUtil.equalsIgnoreCase(row.get("driver_name"), driverName)){
                    layout.setDefaultsViaRow(row);
                    break;
                }
            }
        });
    }

    static setLayoutPropsViaFields(carrierId: string, movementId: string, orderId: string, driverName: string, overrideDriverCell: string) {
        return {
            movementId: movementId || "",
            orderId: orderId || "",
            carrierId: carrierId || "",
            messageText: "",
            driverName: driverName,
            messageId: "",
            deliveryMode: "",
            updateMessage: false,
            hours: "",
            recurrenceFrequency: "",
            recurrenceTermination: ""
        };
    }

    static setupDirectionTimeColumn(tableRow: TableRow, modelRow: ModelRow<any>) {
        const labelDirectionImage = tableRow.findComponentById("labelDirectionImage") as Label;
        const labelScheduledSendTime = tableRow.findComponentById("labelScheduledSendTime") as Label;
        const labelCreatedAtDate = tableRow.findComponentById("labelCreatedAtDate") as Label;
        const outgoingMessage = StringUtil.equalsIgnoreCase(modelRow.get("direction"),"O");
        const isScheduled = StringUtil.equalsIgnoreCase(modelRow.get("scheduled_message"),"Y");
        const isRecurringScheduled = StringUtil.equalsIgnoreCase(modelRow.get("scheduled_message"),"Y") && StringUtil.equalsIgnoreCase(modelRow.get("recurrence_enabled"),"Y");
        const messageRead = StringUtil.equalsIgnoreCase(modelRow.get("message_read"),"Y");
        if(outgoingMessage){
            labelDirectionImage.imageName = SmsUtility.OUTGOING;
            labelDirectionImage.text = SmsUtility.OUTGOING_CAPTION;
            if(isRecurringScheduled){
                labelDirectionImage.imageName = SmsUtility.RECURRING_SCHEDULED;
                labelDirectionImage.text = SmsUtility.OUTGOING_RECURRING_CAPTION;
            }else if(isScheduled){
                labelDirectionImage.imageName = SmsUtility.SCHEDULED_ICON;
                labelDirectionImage.text = SmsUtility.OUTGOING_CAPTION;
            }
        }else{
            if(messageRead){
                labelDirectionImage.imageName = SmsUtility.INCOMING_MESSAGE_READ;
            }else {
                labelDirectionImage.imageName = SmsUtility.INCOMING_MESSAGE_UNREAD;
            }
            labelDirectionImage.text = SmsUtility.INCOMING_TEXT;
        }

        if(isRecurringScheduled && !StringUtil.isEmptyString(modelRow.get("recurrence_frequency")) && !StringUtil.isEmptyString(modelRow.get("delivery_mode"))){
            let messagePrefix = "Every ";
            messagePrefix = "Every" + SmsUtility.buildHourString(modelRow) + SmsUtility.getTerminationMethod(modelRow.get("recurrence_termination"));
            labelScheduledSendTime.text = messagePrefix;
        }

        if(!isRecurringScheduled){
            labelCreatedAtDate.text = SmsUtility.formatDate(modelRow.get("created_at_date"));
        }else{
            labelCreatedAtDate.visible = false;
        }
    }

    static setupStatusColumn(tableRow: TableRow, modelRow: ModelRow<any>) {
        const labelMessageStatus = tableRow.findComponentById("labelMessageStatus") as Label;
        const outgoingMessage = StringUtil.equalsIgnoreCase(modelRow.get("direction"),"O");
        const isScheduled = !StringUtil.equalsIgnoreCase(modelRow.get("delivery_mode"),"N") && !StringUtil.equalsIgnoreCase(modelRow.get("scheduled_message"),"N");
        const isRecurringScheduled = !StringUtil.equalsIgnoreCase(modelRow.get("delivery_mode"),"N") && StringUtil.equalsIgnoreCase(modelRow.get("recurrence_enabled"),"Y");
        const messageRead = StringUtil.equalsIgnoreCase(modelRow.get("message_read"),"Y");
        const isCancelled = StringUtil.equalsIgnoreCase(modelRow.get("cancelled_scheduled_message"),"Y") || !StringUtil.isEmptyString(modelRow.get("canceled_date"));
        const labelStatusDate = tableRow.findComponentById("labelStatusDate") as Label;
        const imageUnreadMessageIcon = tableRow.findComponentById("imageUnreadMessageIcon") as Image;
        const status = modelRow.get("status");

        if(outgoingMessage){
            labelMessageStatus.imageName = SmsUtility.getImageName(status,isScheduled,isRecurringScheduled,isCancelled);
            labelMessageStatus.text = SmsUtility.getText(status,isScheduled,isRecurringScheduled,isCancelled,modelRow);
            labelMessageStatus.imageColor = SmsUtility.getMessageStatusColor(status,isCancelled);
            labelMessageStatus.color = SmsUtility.getMessageStatusColor(status, isCancelled);
            labelMessageStatus.tooltip = StringUtil.equalsIgnoreCase(modelRow.get("status"),"F") ? modelRow.get("error_detail") : modelRow.get("cancel_detail");
            SmsUtility.setLastStatusUpdateDate(labelStatusDate,isCancelled,isScheduled,isRecurringScheduled,modelRow);
        }else{
            labelMessageStatus.imageName = messageRead ? SmsUtility.INCOMING_MESSAGE_READ : SmsUtility.INCOMING_MESSAGE_UNREAD;
            labelMessageStatus.text = SmsUtility.RECEIVED;
            labelMessageStatus.imageColor = SmsUtility.SUCCESS;
            labelMessageStatus.color = SmsUtility.SUCCESS;
            labelStatusDate.text = SmsUtility.formatDate(modelRow.get("created_at_date"));
            if(!messageRead && !StringUtil.isEmptyString(modelRow.get("message_text"))){
                imageUnreadMessageIcon.visible = true;
            }

        }
    }

    static setupMessageColumn(messageText: Label, modelRow: ModelRow<any>) {
        const directionIsIncoming =  StringUtil.equalsIgnoreCase(modelRow.get("direction"),"I");
        messageText.fontBold = directionIsIncoming && StringUtil.equalsIgnoreCase(modelRow.get("message_read"),"N");
    }

    static setLastStatusUpdateDate(labelStatusDate: Label, isCancelled: boolean, isScheduled: boolean,isRecurring:boolean, modelRow: ModelRow<any>) {
        labelStatusDate.visible = true;
        const nextScheduledDate = modelRow.get("next_scheduled_send_date");
        const scheduledSendDate = modelRow.get("scheduled_send_date");
        const deliveredDate = modelRow.get("delivered_date");
        const canceledDate = modelRow.get("canceled_date");
        const errorDate = modelRow.get("error_date");
        const recurringSmsCompDate = modelRow.get("recurring_sms_comp_date");

        if(StringUtil.equalsIgnoreCase("F",modelRow.get("status"))){
            labelStatusDate.text = SmsUtility.formatDate(errorDate);
        }else if(isCancelled){
            labelStatusDate.text = SmsUtility.formatDate(canceledDate);
        }else if(isScheduled && !StringUtil.equalsIgnoreCase(modelRow.get("message_sent"),"Y") && !isRecurring){
            labelStatusDate.text = SmsUtility.formatDate(scheduledSendDate);
        }else if(isRecurring){
            if(StringUtil.isEmptyString(recurringSmsCompDate)) {
                labelStatusDate.text = !StringUtil.isEmptyString(nextScheduledDate)
                    ? SmsUtility.formatDate(nextScheduledDate)
                    : SmsUtility.formatDate(scheduledSendDate);
            }else{
                labelStatusDate.text =SmsUtility.formatDate(recurringSmsCompDate);
            }
        }else if(!StringUtil.isEmptyString(deliveredDate)){
            labelStatusDate.text = SmsUtility.formatDate(deliveredDate);
        }else if(StringUtil.isEmptyString(deliveredDate) && StringUtil.equalsIgnoreCase("P",modelRow.get("status"))){
            labelStatusDate.text = !StringUtil.isEmptyString(nextScheduledDate)
                ? SmsUtility.formatDate(nextScheduledDate)
                : SmsUtility.formatDate(scheduledSendDate);
        }else{
            labelStatusDate.visible = false;
        }
    }

    static getMessageStatusColor(status: string, isCancelled: boolean) {
        const statusMap = new Map<string, string>([
            ["D", SmsUtility.SUCCESS],
            ["P", SmsUtility.PRIMARY],
            ["F", SmsUtility.ERROR],
            ["C", SmsUtility.PRIMARY]
        ]);

        if(isCancelled){
            return SmsUtility.WARNING;
        }

        return statusMap.get(status) || "";
    }


    static getImageName(status: string, isScheduled: boolean, isRecurringScheduled: boolean, isCancelled: boolean):
        ImageName {
        if (StringUtil.equalsIgnoreCase(status,"F")) {
            return SmsUtility.FAILED_ICON;
        } else if (isScheduled) {
            return SmsUtility.SCHEDULED_ICON;
        } else {
            return SmsUtility.OUTGOING;
        }
    }

    static getText(status: string, isScheduled: boolean, isRecurringScheduled: boolean, isCancelled: boolean, modelRow: ModelRow<any>): string {
        // Map status codes to text descriptions
        const statusTextMap: Record<string, string> = {
            F: SmsUtility.FAILED,
            P: SmsUtility.PENDING,
            D: SmsUtility.SENT,
        };

        const prefix = isRecurringScheduled && !StringUtil.isEmptyString(modelRow.get("next_scheduled_send_date")) ? SmsUtility.RECURRING : isScheduled  ? SmsUtility.SCHEDULED : "";

        if (isCancelled && isRecurringScheduled) {
            return  SmsUtility.CANCELLED;
        }else if(isCancelled){
            return prefix ? `${prefix} ${SmsUtility.CANCELLED}`.trim() : SmsUtility.CANCELLED;
        }


        const statusText = statusTextMap[status.toUpperCase()] || "";

        if(isScheduled && !isRecurringScheduled) {
            if (StringUtil.equalsIgnoreCase(statusText, SmsUtility.PENDING)) {
                return prefix;
            }
        }

        if(isRecurringScheduled){
            if(StringUtil.isEmptyString(modelRow.get("recurring_sms_comp_date"))) {
                if(StringUtil.equalsIgnoreCase(modelRow.get("status"),"F")){
                    return statusText;
                }
                return prefix;
            }else{
                return statusText;
            }
        }




        return prefix ? `${prefix} ${statusText}`.trim() : statusText;
    }

    static getTerminationMethod(recurrenceTermination: string) {
        if(StringUtil.equalsIgnoreCase(recurrenceTermination,"C")){
            return SmsUtility.ARRIVAL_AT_CONSIGNEE;
        }else if(StringUtil.equalsIgnoreCase(recurrenceTermination,"L")){
            return SmsUtility.DELIVERY;
        }
    }
    static buildHourString(modelRow: any) {
        if(modelRow.get("recurrence_frequency") == 1){
            return " hour until ";
        }else{
            return " " + modelRow.get("recurrence_frequency") + " hours until ";
        }
    }


    static setupToFromColumn(labelRecipient: Label, modelRow: ModelRow<any>) {
        const inboundMessage = StringUtil.equalsIgnoreCase(modelRow.get("direction"),"I");
        if(inboundMessage){
            const prefix = SmsUtility.FROM_PREFIX;
            SmsUtility.setLabelRecipient(modelRow,labelRecipient,prefix);
        }else{
            const prefix = SmsUtility.TO_PREFIX;
            SmsUtility.setLabelRecipient(modelRow,labelRecipient,prefix);
        }
    }

    static setLabelRecipient(modelRow: ModelRow<any>, labelRecipient: Label, prefix: string) {
        if (!StringUtil.isEmptyString(modelRow.get("recipient_name"))) {
            labelRecipient.text = prefix + modelRow.get("recipient_name");
        }else if(!StringUtil.isEmptyString(modelRow.get("driver_name"))){
            labelRecipient.text = prefix + modelRow.get("driver_name");
        }else{
            labelRecipient.text = prefix;
        }
    }

    static showEditButton(modelRow: ModelRow<any>) {
        const isCancelled = StringUtil.equalsIgnoreCase(modelRow.get("cancelled_scheduled_message"),"Y") || !StringUtil.isEmptyString(modelRow.get("canceled_date"));

        return StringUtil.equalsIgnoreCase(modelRow.get("direction"),"O")
            && StringUtil.equalsIgnoreCase(modelRow.get("scheduled_message"),"Y")
            && !isCancelled;
    }

    static showDeleteButton(modelRow: ModelRow<any>) {
        const messageIsSent = StringUtil.equalsIgnoreCase(modelRow.get("message_sent"),"Y");
        const statusNotDelivered = !StringUtil.equalsIgnoreCase(modelRow.get("status"), "D");
        const directionIsOutgoing =  StringUtil.equalsIgnoreCase(modelRow.get("direction"),"O");
        const isScheduled = StringUtil.equalsIgnoreCase(modelRow.get("scheduled_message"),"Y");
        const isRecurringScheduled = StringUtil.equalsIgnoreCase(modelRow.get("scheduled_message"),"Y") && StringUtil.equalsIgnoreCase(modelRow.get("recurrence_enabled"),"Y")
        const isCancelled = StringUtil.equalsIgnoreCase(modelRow.get("cancelled_scheduled_message"),"Y") || !StringUtil.isEmptyString(modelRow.get("canceled_date"));

        if(isScheduled || isRecurringScheduled){
            return !messageIsSent;
        }

        return (messageIsSent && statusNotDelivered && directionIsOutgoing) || isCancelled;
    }

    static showCancelButton(modelRow: ModelRow<any>) {
        const isRecurringScheduled = StringUtil.equalsIgnoreCase(modelRow.get("scheduled_message"),"Y") && StringUtil.equalsIgnoreCase(modelRow.get("recurrence_enabled"),"Y")
        if(isRecurringScheduled){
            return StringUtil.equalsIgnoreCase(modelRow.get("direction"),"O")
                && !StringUtil.equalsIgnoreCase(modelRow.get("cancelled_scheduled_message"),"Y")
                && StringUtil.isEmptyString(modelRow.get("recurring_sms_comp_date"));
        }

        return StringUtil.equalsIgnoreCase(modelRow.get("message_sent"),"N")
            && StringUtil.equalsIgnoreCase(modelRow.get("direction"),"O")
            && StringUtil.equalsIgnoreCase(modelRow.get("scheduled_message"),"Y")
            && !StringUtil.equalsIgnoreCase(modelRow.get("cancelled_scheduled_message"),"Y")
            && StringUtil.isEmptyString(modelRow.get("recurring_sms_comp_date"));
    }

    static showReplyButton(modelRow: ModelRow<any>) {
        return StringUtil.equalsIgnoreCase(modelRow.get("direction"),"I");
    }

    static showResendButton(modelRow: ModelRow<any>) {
        const isCancelled = StringUtil.equalsIgnoreCase(modelRow.get("cancelled_scheduled_message"),"Y") || !StringUtil.isEmptyString(modelRow.get("canceled_date"));

        return  StringUtil.equalsIgnoreCase(modelRow.get("direction"),"O")
            &&  StringUtil.equalsIgnoreCase(modelRow.get("message_sent"),"Y")
            && !isCancelled;
    }


    static showMarkAsReadUnreadButton(modelRow: ModelRow<any>) {
        return StringUtil.equalsIgnoreCase(modelRow.get("direction"),"I");
    }

    static setMarkAsRead(modelRow: ModelRow<any>) {
        const messageReadStatus = modelRow.get("message_read");
        if(StringUtil.equalsIgnoreCase(modelRow.get("direction"),"I")){
            if(StringUtil.equalsIgnoreCase(messageReadStatus,"Y")){
                return this.MARK_AS_UNREAD;
            }else{
                return this.MARK_AS_READ;
            }
        }
        return "" ;
    }

    static setMarkAsReadImage(modelRow: ModelRow<any>): ImageName {
        const messageReadStatus =  modelRow.get("message_read");
        if(StringUtil.equalsIgnoreCase(modelRow.get("direction"),"I")){
            if(StringUtil.equalsIgnoreCase(messageReadStatus,"Y")){
                return this.ENVELOPE_UNREAD;
            }else{
                return this.ENVELOPE_READ;
            }
        }
        return null;
    }

    static getContextMenuItems(tableRow: TableRow, allOptions: boolean): Partial<ContextMenuLabelProps>[] {
        const { table } = tableRow;
        let menuItems: any[];
        if (table.selectedRow !== tableRow) {
            table.selectedRow = tableRow;
        }
        const modelRow = tableRow.data as ModelRow;

        if(allOptions) {
            menuItems = [
                {id: 1, text: "Edit Message", image: ImageName.pencil, visible: SmsUtility.showEditButton},
                {id: 2, text: "Delete Message", image: ImageName.delete, visible: SmsUtility.showDeleteButton},
                {id: 3, text: "Cancel Message", image: ImageName.x, visible: SmsUtility.showCancelButton},
                {id: 4, text: "Reply Message", image: ImageName.messagesReply, visible: SmsUtility.showReplyButton},
                {id: 5, text: "Resend Message", image: ImageName.resendMessage, visible: SmsUtility.showResendButton},
                {
                    id: 6,
                    text: SmsUtility.setMarkAsRead(modelRow),
                    image: SmsUtility.setMarkAsReadImage(modelRow),
                    visible: SmsUtility.showMarkAsReadUnreadButton
                }
            ];
        }else{
            menuItems = [
                {id: 1, text: "Edit Message", image: ImageName.pencil, visible: SmsUtility.showEditButton},
                {id: 2, text: "Delete Message", image: ImageName.delete, visible: SmsUtility.showDeleteButton},
                {id: 3, text: "Cancel Message", image: ImageName.x, visible: SmsUtility.showCancelButton},
                {
                    id: 6,
                    text: SmsUtility.setMarkAsRead(modelRow),
                    image: SmsUtility.setMarkAsReadImage(modelRow),
                    visible: SmsUtility.showMarkAsReadUnreadButton
                }
            ];
        }

        return ContextMenuItemCreator.fromButtons(
            ...menuItems.map(item => {
                const button = this.getButton(item.id);
                const labelProps: Partial<ContextMenuLabelProps> = {
                    text: item.text,
                    imageName: item.image,
                    visible: item.visible(modelRow),
                    onClick: () => this.handleButtonClick(item.id, table.selectedRow)
                };
                return { button, ...labelProps };
            })
        );
    }

    private static getButton(id: number): Button {
        const buttons = {
            1: { id: "editBtn", caption: "Edit Message" },
            2: { id: "deleteBtn", caption: "Delete Message" },
            3: { id: "cancelBtn", caption: "Cancel Message" },
            4: { id: "replyBtn", caption: "Reply Message" },
            5: { id: "resendBtn", caption: "Resend Message" },
            6: { id: "markMessageStatusBtn", caption: "Change Message Status" }
        };
        return new Button(buttons[id]);
    }

    private static handleButtonClick(id: number, selectedRow: TableRow) {
        const actions = {
            1: () => this.buttonEditOnClick(selectedRow),
            2: () => this.buttonRightClickDelete(selectedRow),
            3: () => this.buttonRightClickCancel(selectedRow),
            4: () => this.buttonRightClickReply(selectedRow),
            5: () => this.buttonRightClickResend(selectedRow),
            6: () => this.buttonRightClickMarkMessageStatus(selectedRow)
        };
        actions[id]?.();
    }

    static buttonEditOnClick( selectedRow: TableRow) {
        const modelRow = selectedRow.data;
        SmsUtility.showSendSmsMessageSlideout(SmsUtility.setLayoutProps(modelRow,true,false));
    }

    static async buttonRightClickDelete(selectedRow: TableRow) {
        const modelRow = selectedRow.data;
        await SmsUtility.processDeleteMessage(modelRow, selectedRow);
    }

    static async buttonRightClickCancel(selectedRow: TableRow) {
        const modelRow = selectedRow.data;
        await SmsUtility.processCancelMessage(selectedRow, modelRow, null);
    }

    static buttonRightClickReply(selectedRow: TableRow) {
        const modelRow = selectedRow.data;
        SmsUtility.showSendSmsMessageSlideout(SmsUtility.setLayoutProps(modelRow,false,true));
    }

    static buttonRightClickResend(selectedRow: TableRow) {
        const modelRow = selectedRow.data;
        SmsUtility.showSendSmsMessageSlideout(SmsUtility.setLayoutProps(modelRow,false,false));
    }

    static async buttonRightClickMarkMessageStatus(selectedRow: TableRow) {
        const modelRow = selectedRow.data;
        await SmsUtility.updateMessageStatus(selectedRow, modelRow);
    }
    static updateButtonVisibility(isVisible: boolean, button: Button) {
        button.visible = isVisible;
    }

    static async processCancelMessage(tableRow: TableRow, modelRow: any, button: Button, doAfterAction?: () => void) {
        const labelMessageStatus = tableRow.findComponentById ( "labelMessageStatus") as Label;
        const messageRowId = modelRow.get("parent_row_id");
        if (await CommonDialogs.showYesNo("Are you sure you want to cancel this SMS Message?", "Cancel SMS Message")) {
            try {
                const response = await Api.post("lme/powerbroker/cancel-sms-message", {
                    message_row_id: messageRowId
                });
                if (response?.data?.[0]?.message_cancelled) {
                    Toast.showSuccessToast("SMS message has been canceled.", null, null, {millisUntilDismissal: 5000});
                    labelMessageStatus.text = "Canceled";
                    if(button !== undefined && button !== null) {
                        button.visible = false;
                    }
                    modelRow.set("cancelled_scheduled_message", "Y");

                    SmsUtility.showCancelButton(modelRow);
                    SmsUtility.setupStatusColumn(tableRow,modelRow);
                    const labelStatusDate = tableRow.findComponentById("labelStatusDate") as Label;
                    const canceledDate = response?.data?.[0].message_cancel_date;
                    labelStatusDate.text = SmsUtility.formatDate(canceledDate);
                    labelMessageStatus.tooltip = response?.data?.[0].message_cancel_detail || "";
                    if(doAfterAction) {
                        doAfterAction();
                    }
                } else {
                    Snackbar.showSnackbar("Failed to cancel message.");
                }
            } catch (error) {
                Snackbar.showSnackbar("Failed to cancel message.");
            }
        }
    }

   static async processDeleteMessage(modelRow: any, tableRow: TableRow) {
        const rowId = modelRow.get("parent_row_id");
        if (await CommonDialogs.showYesNo("Are you sure you want to delete this SMS Message?", "Delete SMS Message")) {
            try {
                const response = await Api.post("lme/powerbroker/delete-sms-message", {
                    row_id: rowId
                });
                if (response?.data?.[0]?.message_deleted) {
                    Toast.showSuccessToast("Message has been deleted.", null, null, {millisUntilDismissal: 5000});
                    tableRow.visible = false;
                } else {
                    Snackbar.showSnackbar("Failed to delete message.");
                }
            } catch (error) {
                Snackbar.showSnackbar("Failed to delete message.");
            }
        }
    }

    static async updateMessageStatus(tableRow: TableRow, modelRow: any) {
        const labelDirectionImage = tableRow.findComponentById("labelDirectionImage") as Label;
        const messageRead = StringUtil.equalsIgnoreCase(modelRow.get("message_read"), "Y");
        const messageId = modelRow.get("message_id");
        const response = await Api.post("lme/powerbroker/update-message-read", {
            message_id: messageId,
            message_read: !messageRead,
            movement_id: modelRow.get("movement_id")
        });

        if (response.data?.[0].message_updated) {
            if(!StringUtil.equalsIgnoreCase(modelRow.get("message_read"),"N")) {
                this.setInboundUnreadStatus(tableRow, modelRow, labelDirectionImage);
            }else {
                this.setInboundReadStatus(tableRow, modelRow, labelDirectionImage);
            }
        }
    }

    static setInboundUnreadStatus(tableRow: TableRow, modelRow: any, labelDirectionImage: Label) {
        const imageUnreadMessageIcon = tableRow.findComponentById("imageUnreadMessageIcon") as Image;
        const messageText = tableRow.findComponentById("labelMessageText") as Label;
        labelDirectionImage.imageName = this.INCOMING_MESSAGE_READ;
        if(messageText?.text){
            imageUnreadMessageIcon.visible = true;
            messageText.fontBold = true;
        }
        tableRow.data.set("message_read", "N");
    }

    static setInboundReadStatus(tableRow: TableRow, modelRow: any,labelDirectionImage: Label) {
        const imageUnreadMessageIcon = tableRow.findComponentById("imageUnreadMessageIcon") as Image;
        const messageText = tableRow.findComponentById("labelMessageText") as Label;
        labelDirectionImage.imageName = this.INCOMING_MESSAGE_UNREAD;
        imageUnreadMessageIcon.visible = false;
        tableRow.data.set("message_read", "Y");
        messageText.fontBold = false;
    }
}
